import clsx from "clsx";
import { motion } from "framer-motion";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { useLabels } from "../../../contexts/labels";
import { WorldAreaTabsKeys } from "./types";

export interface WorldAreaTabsProps {
  selectedTabId: WorldAreaTabsKeys;
  onClickTab: (tabId: WorldAreaTabsKeys) => void;
  shouldPlayAnimation?: boolean;
  tabsKeys: WorldAreaTabsKeys[];
  variant: "pills" | "default";
}

export const WorldAreaTabs: FC<WorldAreaTabsProps> = ({
  selectedTabId,
  onClickTab,
  shouldPlayAnimation = true,
  tabsKeys,
  variant,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<Array<HTMLDivElement | null>>([]);
  const [tabIndicatorWidth, setTabIndicatorWidth] = useState(0);
  const [tabIndicatorLeft, setTabIndicatorLeft] = useState(0);
  const [tabIndicatorHeight, setTabIndicatorHeight] = useState(0);
  const [tabIndicatorTop, setTabIndicatorTop] = useState(0);

  const selectedTabIndex = tabsKeys.indexOf(selectedTabId);
  const onSetSlidingIndicatorDimensions = useCallback((selectedTabIndex: number) => {
    const selectedTabRect = tabsRef.current[selectedTabIndex]?.getBoundingClientRect();
    const rootRect = rootRef.current?.getBoundingClientRect();
    if (selectedTabRect) {
      setTabIndicatorWidth(selectedTabRect.width);
      setTabIndicatorLeft(selectedTabRect.left - (rootRect?.left ?? 0));
      setTabIndicatorHeight(selectedTabRect.height);
      setTabIndicatorTop(selectedTabRect.top - (rootRect?.top ?? 0));
    }

    return requestAnimationFrame(() => {
      onSetSlidingIndicatorDimensions(selectedTabIndex);
    });
  }, []);

  useEffect(() => {
    const requestId = onSetSlidingIndicatorDimensions(selectedTabIndex);
    return () => {
      cancelAnimationFrame(requestId);
    };
  }, [onSetSlidingIndicatorDimensions, selectedTabIndex]);

  const { labels } = useLabels();

  return (
    <div className="relative mb-6" ref={rootRef}>
      <div
        className={clsx(
          "flex items-center w-full pb-3",
          variant === "default" ? "justify-between" : "justify-center gap-4"
        )}
      >
        {tabsKeys.map((tabKey, i) => (
          <motion.div
            key={i}
            ref={(el) => (tabsRef.current[i] = el)}
            initial={variant === "default" ? { y: 100 + i * 30 } : undefined}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            animate={shouldPlayAnimation ? { y: 0 } : undefined}
            className={clsx(
              "select-none cursor-pointer",
              variant === "default" && "text-2xl lg:text-3xl mt-2 pr-2",
              variant === "pills" && "rounded-full py-2 px-4 text-2xl",
              variant === "pills" && selectedTabId === tabKey ? "text-primary z-20" : "text-light"
            )}
            onClick={() => {
              onClickTab(tabKey);
            }}
          >
            {labels[`world-tabs-${tabKey.toLowerCase()}`]}
          </motion.div>
        ))}
      </div>
      {/* Sliding indicator */}
      <span
        className={clsx(
          "absolute bottom-0 block transition-all duration-300",
          variant === "pills" ? "bg-light rounded-full" : "bg-primary h-1"
        )}
        style={{
          left: tabIndicatorLeft,
          width: tabIndicatorWidth,
          top: variant === "pills" ? tabIndicatorTop : undefined,
          height: variant === "pills" ? tabIndicatorHeight : undefined,
        }}
      />
    </div>
  );
};
